// 企業従業員管理用カスタムフック

import { useReducer, useCallback, useEffect } from 'react';
import { useToast } from './useToast';
import employeeService from '../services/employeeService';
import {
  CompanyEmployee,
  AddEmployeeFormData,
  EmployeeListFilters,
  EmployeeManagementState,
  EmployeeManagementAction,
  CompanyEmployeeRole,
  EmployeeStatus,
} from '../types/employee';

// 初期状態
const initialState: EmployeeManagementState = {
  employees: [],
  loading: false,
  filters: {
    sortBy: 'created_at',
    sortOrder: 'desc',
    page: 1,
    limit: 20,
    status: undefined,
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  modals: {
    addEmployee: false,
    editEmployee: false,
    employeeDetail: false,
  },
};

// リデューサー
function employeeManagementReducer(
  state: EmployeeManagementState,
  action: EmployeeManagementAction
): EmployeeManagementState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_EMPLOYEES':
      return { ...state, employees: action.payload };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload },
      };

    case 'OPEN_ADD_MODAL':
      return {
        ...state,
        modals: { ...state.modals, addEmployee: true },
      };

    case 'CLOSE_ADD_MODAL':
      return {
        ...state,
        modals: { ...state.modals, addEmployee: false },
      };


    case 'OPEN_EDIT_MODAL':
      return {
        ...state,
        modals: { ...state.modals, editEmployee: true },
        selectedEmployee: action.payload,
      };

    case 'CLOSE_EDIT_MODAL':
      return {
        ...state,
        modals: { ...state.modals, editEmployee: false },
        selectedEmployee: undefined,
      };

    case 'OPEN_DETAIL_MODAL':
      return {
        ...state,
        modals: { ...state.modals, employeeDetail: true },
        selectedEmployee: action.payload,
      };

    case 'CLOSE_DETAIL_MODAL':
      return {
        ...state,
        modals: { ...state.modals, employeeDetail: false },
        selectedEmployee: undefined,
      };

    case 'ADD_EMPLOYEE':
      return {
        ...state,
        employees: [action.payload, ...state.employees],
      };

    case 'UPDATE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.map((emp) =>
          emp.id === action.payload.id ? action.payload : emp
        ),
      };


    default:
      return state;
  }
}

// カスタムフック
export function useEmployeeManagement(companyId: number) {
  const [state, dispatch] = useReducer(employeeManagementReducer, initialState);
  const toast = useToast();

  // 従業員一覧を取得
  const fetchEmployees = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await employeeService.getEmployees(
        companyId,
        state.filters
      );

      dispatch({ type: 'SET_EMPLOYEES', payload: response.data.employees });
      dispatch({
        type: 'SET_PAGINATION',
        payload: {
          page: response.data.page,
          total: response.data.total,
          totalPages: response.data.totalPages,
        },
      });
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('従業員一覧の取得に失敗しました');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [companyId, state.filters, toast]);

  // フィルター変更を監視して再取得
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters.search, state.filters.role, state.filters.status, state.filters.sortBy, state.filters.sortOrder, state.filters.page, companyId]);

  // 検索・フィルター更新
  const updateFilters = useCallback(
    (newFilters: Partial<EmployeeListFilters>) => {
      dispatch({ type: 'SET_FILTERS', payload: { ...newFilters, page: 1 } });
    },
    []
  );

  // ページネーション更新
  const updatePage = useCallback((page: number) => {
    dispatch({ type: 'SET_FILTERS', payload: { page } });
  }, []);

  // ソート更新
  const updateSort = useCallback(
    (sortBy: 'name' | 'created_at') => {
      const newSortOrder =
        state.filters.sortBy === sortBy && state.filters.sortOrder === 'desc'
          ? 'asc'
          : 'desc';

      dispatch({
        type: 'SET_FILTERS',
        payload: { sortBy, sortOrder: newSortOrder, page: 1 },
      });
    },
    [state.filters.sortBy, state.filters.sortOrder]
  );

  // 従業員追加
  const addEmployee = useCallback(
    async (employeeData: AddEmployeeFormData) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const response = await employeeService.addEmployee(
          companyId,
          employeeData
        );

        // 新しい従業員をリストに追加（簡易実装）
        const newEmployee: CompanyEmployee = {
          id: response.data.employeeId,
          companyId,
          user: {
            id: response.data.userId,
            fullName: employeeData.fullName,
            email: employeeData.email,
          },
          role: employeeData.role,
          department: employeeData.department,
          jobTitle: employeeData.jobTitle,
          status: EmployeeStatus.ACTIVE,
          createdAt: new Date().toISOString(),
        };

        dispatch({ type: 'ADD_EMPLOYEE', payload: newEmployee });
        dispatch({ type: 'CLOSE_ADD_MODAL' });

        toast.success(response.data.message);

        if (response.data.temporaryPassword) {
          toast.success(
            `仮パスワード: ${response.data.temporaryPassword}`,
            10000
          );
        }

        // リストを再取得（実際のデータで更新）
        await fetchEmployees();
      } catch (error) {
        console.error('Failed to add employee:', error);
        toast.error(
          error instanceof Error ? error.message : '従業員の追加に失敗しました'
        );
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [companyId, fetchEmployees]
  );

  // 従業員更新
  const updateEmployee = useCallback((updatedEmployee: CompanyEmployee) => {
    dispatch({ type: 'UPDATE_EMPLOYEE', payload: updatedEmployee });
    toast.success('従業員情報を更新しました');
  }, []);


  const updateEmployeeStatus = useCallback(
    async (employeeId: number, status: EmployeeStatus) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const response = await employeeService.updateEmployee(
          companyId,
          employeeId,
          {
            status,
          }
        );

        // ローカルステートを更新
        dispatch({ type: 'UPDATE_EMPLOYEE', payload: response.data.employee });
        
        // 確実に反映させるため、従業員一覧を再取得
        await fetchEmployees();
        
        toast.success('従業員ステータスを更新しました');
      } catch (error) {
        console.error('❌ Failed to update employee status:', error);
        toast.error(
          error instanceof Error
            ? error.message
            : '従業員ステータスの更新に失敗しました'
        );
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [companyId, fetchEmployees]
  );

  // モーダル操作
  const openAddModal = useCallback(() => {
    dispatch({ type: 'OPEN_ADD_MODAL' });
  }, []);

  const closeAddModal = useCallback(() => {
    dispatch({ type: 'CLOSE_ADD_MODAL' });
  }, []);


  const openEditModal = useCallback((employee: CompanyEmployee) => {
    dispatch({ type: 'OPEN_EDIT_MODAL', payload: employee });
  }, []);

  const closeEditModal = useCallback(() => {
    dispatch({ type: 'CLOSE_EDIT_MODAL' });
  }, []);

  const openDetailModal = useCallback((employee: CompanyEmployee) => {
    dispatch({ type: 'OPEN_DETAIL_MODAL', payload: employee });
  }, []);

  const closeDetailModal = useCallback(() => {
    dispatch({ type: 'CLOSE_DETAIL_MODAL' });
  }, []);

  return {
    // 状態
    employees: state.employees,
    loading: state.loading,
    filters: state.filters,
    pagination: state.pagination,
    modals: state.modals,
    selectedEmployee: state.selectedEmployee,

    // アクション
    fetchEmployees,
    updateFilters,
    updatePage,
    updateSort,
    addEmployee,
    updateEmployee,
    updateEmployeeStatus,

    // モーダル操作
    openAddModal,
    closeAddModal,
    openEditModal,
    closeEditModal,
    openDetailModal,
    closeDetailModal,
  };
}
